import {match, Pattern} from 'ts-pattern';

import {Nullish} from 'shared/web';

import {ListFeaturesItemProps} from '../components/ListFeatures/components/ListFeaturesItem';
import i18n from '../i18n/i18n';
import {FeatureField} from './getFeatures';

const AVAILABLE = 'AVAILABLE';
const NOT_AVAILABLE = 'NOT_AVAILABLE';
const FUNCTIONAL = 'FUNCTIONAL';
const NON_FUNCTIONAL = 'NON_FUNCTIONAL';
const NON_TESTED = 'NON_TESTED';

export type FeatureState = Pick<ListFeaturesItemProps, 'variant' | 'flagText' | 'tooltipText'>;

/**
 * Maps the equipment state recorded in the inspection to what the certificate shows for it:
 * the icon variant, the inline flag next to the title and the label revealed on hover.
 *
 * | Availability   | Functionality  | Icon                    | Hover                       |
 * | -------------- | -------------- | ----------------------- | --------------------------- |
 * | AVAILABLE      | FUNCTIONAL     | green check             | functionality label         |
 * | AVAILABLE      | NON_FUNCTIONAL | orange exclamation mark | functionality label         |
 * | AVAILABLE      | NON_TESTED     | grey question mark      | functionality label         |
 * | AVAILABLE      | not recorded   | grey question mark      | "Not filled"                |
 * | NOT_AVAILABLE  | —              | red cross               | availability label          |
 * | other / none   | —              | grey question mark      | availability label or "Not filled" |
 */
export function getFeatureState(field: FeatureField | Nullish): FeatureState {
  const notFilledLabel = i18n.t('notFilled');

  return match<[string | Nullish, string | Nullish], FeatureState>([
    field?.value,
    field?.statusValue,
  ])
    .with([AVAILABLE, FUNCTIONAL], () => ({
      variant: 'ready',
      flagText: null,
      tooltipText: field?.statusLabel ?? null,
    }))
    .with([AVAILABLE, NON_FUNCTIONAL], () => ({
      variant: 'damaged',
      flagText: field?.statusLabel ?? null,
      tooltipText: field?.statusLabel ?? null,
    }))
    .with([AVAILABLE, NON_TESTED], () => ({
      variant: 'neutral',
      flagText: field?.statusLabel ?? null,
      tooltipText: field?.statusLabel ?? null,
    }))
    .with([AVAILABLE, Pattern._], () => ({
      variant: 'neutral',
      flagText: null,
      tooltipText: field?.statusLabel ?? notFilledLabel,
    }))
    .with([NOT_AVAILABLE, Pattern._], () => ({
      variant: 'missing',
      flagText: null,
      tooltipText: field?.label ?? null,
    }))
    .otherwise(() => ({
      variant: 'neutral',
      flagText: null,
      tooltipText: field?.label ?? notFilledLabel,
    }));
}
